import * as React from 'react';
import { alpha, Theme, useTheme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import OutlinedInput from '@mui/material/OutlinedInput';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import Chip from '@mui/material/Chip';
import { forwardRef, useMemo, useState } from 'react';
import { Autocomplete, CircularProgress, Collapse, Button, ListSubheader, Menu, Paper, Popper, TextField, Typography } from '@mui/material';
import { ExpandLess, ExpandMore } from "@mui/icons-material";
import { FixedSizeList } from "react-window";
import { SearchBox } from './SearchBox';
import { grey } from '@mui/material/colors';
import { highlightText } from '../tables/BaseTable';
import { useApi } from '../../services/HttpService';
import { CenterSpinner } from './CenterSpinner';

interface InputItem {
  id: number;
  [key: string]: any; // Allows dynamic fields
}

const GroupeMenuItemsByField = (
  inputs: InputItem[],
  groupByField: string,
  selectField: string,
  disabledItems: number[],
  theme: Theme,
  collapsible?: boolean,
): JSX.Element[] => {
  const elements: JSX.Element[] = [];
  const seenGroups = new Set<string>(); // Track which groups have been added
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>({});

  const toggleGroup = (group: string) => {
    setExpandedGroups((prev) => ({ ...prev, [group]: !prev[group] }));
  };

  inputs.forEach((input) => {
    const group = input[groupByField];

    // Add ListSubheader once per group
    if (!seenGroups.has(group)) {
      seenGroups.add(group);

      if (collapsible && group) {
        const isExpanded = expandedGroups[group] ?? false;

        elements.push(
          <ListSubheader
            key={`header-${group}`}
            sx={{ p: 0 }}
          >
            <Paper
              onClick={() => toggleGroup(group)}
              elevation={2}
              sx={{
                backgroundColor: theme.palette.mode === "light" ? grey[300] : "",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                p: "4px 16px",
                cursor: "pointer",
              }}
            >
              {group}
              {isExpanded ? <ExpandLess /> : <ExpandMore />}
            </Paper>
          </ListSubheader>
        );
      } else {
        elements.push(<ListSubheader sx={{ backgroundColor: theme.palette.mode === "light" ? grey[200] : "" }} key={`header-${group}`}>{group}</ListSubheader>);
      }
    }

    // Add MenuItem
    if (collapsible && group) {
      if (expandedGroups[group]) {
        elements.push(
          // <Collapse key={`collapse-${input.id}`} in={expandedGroups[group]} timeout="auto">
          <MenuItem key={input.id} value={input.id} disabled={disabledItems.includes(input.id)}>
            {input[selectField]}
          </MenuItem>
          // </Collapse>
        );
      }
    } else {
      elements.push(
        <MenuItem key={input.id} value={input.id} disabled={disabledItems.includes(input.id)}>
          {input[selectField]}
        </MenuItem>
      );
    }
  });

  return elements;
};

interface multiSelectProps {
  label: string;
  inputs: InputItem[];
  selectField: string;
  selected: number[];
  disabled?: boolean;
  disabledItems?: number[];
  onChange: (ids: number[]) => void
}

export const MultipleSelectChip: React.FC<multiSelectProps> = ({ label, inputs, selectField, selected, disabled, disabledItems, onChange }) => {
  const theme = useTheme();

  const handleChange = (event: SelectChangeEvent<number[]>) => {
    const { value } = event.target;
    const selectedIds = typeof value === 'string' ? value.split(',').map(Number) : value;
    onChange(selectedIds);
  };

  const handleItemClick = (id: number) => {
    const newSelected = selected.includes(id)
      ? selected.filter((itemId) => itemId !== id) // Remove if already selected
      : [...selected, id]; // Add if not selected

    onChange(newSelected);
  };

  const handleClear = (event: React.MouseEvent<HTMLLIElement, MouseEvent>) => {
    onChange([]);
  };

  const getStyles = (id: number, selected: number[], theme: Theme) => {
    const isSelected = selected.includes(id);
    if (!isSelected) return {};

    return {
      fontWeight: isSelected
        ? theme.typography.fontWeightMedium
        : theme.typography.fontWeightRegular,
      // backgroundColor: isSelected
      //   ? theme.palette.primary.main
      //   : 'inherit',
      color: isSelected
        ? theme.palette.primary.contrastText
        : 'inherit',
      backgroundColor: isSelected ? alpha(theme.palette.primary.main, 0.1) : 'inherit',
      "&:hover": { backgroundColor: isSelected ? alpha(theme.palette.primary.main, 0.15) : '' }
    };
  }

  return (
    <FormControl variant='outlined' sx={{ width: '100%' }}>
      <InputLabel id="multiple-chip-label">{label}</InputLabel>
      <Select
        labelId="multiple-chip-label"
        id="multiple-chip"
        multiple
        value={selected}
        // onChange={handleChange}
        disabled={disabled}
        sx={{ "& .MuiSelect-icon": { color: theme.palette.mode === "dark" ? "white" : "black" } }}
        input={<OutlinedInput id="select-multiple-chip" label={label} />}
        renderValue={(selectedIds) => (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {selectedIds.map((id) => {
              const selectedItem = inputs.find((input) => input.id === id);
              return selectedItem ? (
                <Chip key={id} label={selectedItem[selectField]} />
              ) : null;
            })}
          </Box>
        )}
      // MenuProps={MenuProps}
      >
        <MenuItem
          value={null}
          onClick={handleClear}
          sx={{
            fontWeight: theme.typography.fontWeightMedium,
            "&:hover": {
              backgroundColor: alpha(theme.palette.primary.main, 0.2)
            },
          }}
        >
          <em>Fjern alle</em>
        </MenuItem>
        {inputs?.map((input) => {
          const isSelected = selected.includes(input.id);

          return (
            <MenuItem
              key={input.id}
              value={input.id}
              onClick={() => handleItemClick(input.id)}
              disabled={disabledItems?.includes(input.id)}
              sx={{
                "&:hover": {
                  backgroundColor: !isSelected ? alpha(theme.palette.primary.main, 0.2) : ''
                },
              }}
            // style={getStyles(input.id, selected, theme)}
            >
              {input.name}
            </MenuItem>
          )
        })}
      </Select>
    </FormControl>
  );
}

interface singleSelectChipProps {
  label: string;
  inputs: InputItem[];
  selectField: string;
  selectedVariant?: 'standard' | 'chip';
  groupByField?: string; // groups the inputs by this field
  selected: number;
  disabledItems?: number[];
  required?: boolean;
  error?: boolean;
  onChange: (id: number) => void;
  onBlur?: React.FocusEventHandler<HTMLInputElement | HTMLTextAreaElement>;
}

export const SingleSelectChip: React.FC<singleSelectChipProps> = (props: singleSelectChipProps) => {
  const theme = useTheme();
  const {
    label, inputs, selectField,
    selectedVariant = 'standard',
    groupByField = "",
    selected,
    disabledItems = [],
    required = false,
    error = false,
    onChange,
    onBlur = undefined
  } = props;

  const handleChange = (event: SelectChangeEvent<number>) => {
    const { value } = event.target;
    console.log("singleSelectChipProps change:", value)
    const selectedId = typeof value === 'string' ? Number(value) : value;
    onChange(selectedId);
  };

  const displaySelected = (selectedId) => {
    let content = null;
    if (selectedId > 0) {
      const selectedItem = inputs.find((input) => input.id === selectedId);
      if (selectedItem) {
        selectedVariant === 'standard' ?
          content = selectedItem[selectField] :
          content = <Chip key={selectedId} label={selectedItem[selectField]} />;
      }
    }

    return <>{content}</>;
  }

  const handleNoneSelect = () => {
    onChange(0);
  };

  return (
    <FormControl variant='outlined' sx={{ width: '100%' }}>
      <InputLabel id="single-chip-label" error={error}>{label}{required ? " *" : ""}</InputLabel>
      <Select
        labelId="single-chip-label"
        id="single-chip"
        value={selected > 0 ? selected : ''}
        onChange={handleChange}
        onBlur={onBlur}
        error={error}
        sx={{ "& .MuiSelect-icon": { color: theme.palette.mode === "dark" ? "white" : "black" } }}
        input={<OutlinedInput id="single-select-chip" label={`${label}${required ? " *" : ""}`} />}
        renderValue={(selectedId) => (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {displaySelected(selectedId)}
          </Box>
        )}
      >
        <MenuItem
          value={0}
          onClick={handleNoneSelect}
        >
          <em>Ingen</em>
        </MenuItem>
        {groupByField !== "" ? (
          GroupeMenuItemsByField(inputs, groupByField, selectField, disabledItems, theme, true)
        ) : (
          inputs?.map((input) => (
            <MenuItem
              key={input.id}
              value={input.id}
              disabled={disabledItems?.includes(input.id)}
            >
              {input[selectField]}
            </MenuItem>
          ))
        )}
      </Select>
    </FormControl>
  );
}

interface singleSelectSearchProps {
  label: string;
  inputs: InputItem[];
  selectField: string;
  selected: number;
  selectedVariant?: 'standard' | 'chip';
  mode?: 'standard' | 'chip'; // uses 
  required?: boolean;
  error?: boolean;
  onChange: (id: number) => void;
  onBlur?: React.FocusEventHandler<HTMLInputElement | HTMLTextAreaElement>;
}

/**
 * Dropdown select with search. Use this when there are a lot of options.
 *  - It uses react-window FixedSizeList for fast render and open and close animation.
 */
export const SingleSelectSearch: React.FC<singleSelectSearchProps> = (props: singleSelectSearchProps) => {
  const theme = useTheme();
  const {
    label, selectField, selected,
    selectedVariant = 'standard',
    required = false,
    error = false,
    onChange,
    onBlur = undefined
  } = props;

  const [options, setOptions] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [open, setOpen] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null); // For controlling menu open/close
  const searchFieldRef = React.useRef<HTMLInputElement>(null);

  React.useEffect(() => {
    const noneOption = { id: 0 };
    noneOption[selectField] = 'Ingen';
    setOptions([noneOption, ...props.inputs]);
  }, [props.inputs])

  const filteredInputs = useMemo(
    () => options.filter((input) =>
      input[selectField].toLowerCase().includes(searchTerm.toLowerCase())
    ),
    [options, searchTerm, selectField]
  );

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };

  const displaySelected = (selectedId) => {
    let content = null;
    if (selectedId > 0) {
      const selectedItem = options.find((input) => input.id === selectedId);
      if (selectedItem) {
        selectedVariant === 'standard' ?
          content = selectedItem[selectField] :
          content = <Chip key={selectedId} label={selectedItem[selectField]} />;
      }
    }

    return <>{content}</>;
  }

  const handleChange = (value: number) => {
    onChange(value);
    handleClose();
  };

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    setMenuAnchor(event.currentTarget);
    setTimeout(() => {
      if (searchFieldRef.current) {
        searchFieldRef.current.focus();
        searchFieldRef.current.select();
      }
    }, 0);
  };

  const handleClose = () => {
    setMenuAnchor(null);
  };

  const renderRow = ({ index, style }: { index: number; style: React.CSSProperties }) => {
    const item = filteredInputs[index];
    const isSelected = item.id === selected;

    return (
      <MenuItem
        key={item.id}
        value={item.id}
        sx={{
          fontWeight: theme.typography.fontWeightMedium,
          backgroundColor: isSelected ? alpha(theme.palette.primary.main, 0.1) : 'inherit',
          "&:hover": { backgroundColor: isSelected ? alpha(theme.palette.primary.main, 0.15) : '' }
        }}
        style={style}
        onClick={() => handleChange(item.id)}
      >
        {highlightText(item[selectField], searchTerm, theme.palette.mode)}
      </MenuItem>
    );
  };

  return (
    <FormControl variant='outlined' sx={{ width: '100%' }}>
      <InputLabel id="single-chip-label" error={error}>{label}{required ? " *" : ""}</InputLabel>
      <Select
        labelId="single-chip-label"
        id="single-chip"
        value={selected > 0 ? selected : ''}
        open={Boolean(menuAnchor)}
        onChange={(event) => event.preventDefault()}
        onOpen={handleOpen}
        onClose={handleClose}
        onBlur={onBlur}
        error={error}
        sx={{ "& .MuiSelect-icon": { color: theme.palette.mode === "dark" ? "white" : "black" } }}
        input={<OutlinedInput id="single-select-chip" label={`${label}${required ? " *" : ""}`} />}
        renderValue={(selectedId) => (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {displaySelected(selectedId)}
          </Box>
        )}
        MenuProps={{
          disablePortal: true,
        }} // Disable default dropdown behavior
      >
        <MenuItem disabled value="" sx={{ display: "Ingen" }} />
      </Select>
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleClose}
        slotProps={{
          paper: {
            style: {
              width: menuAnchor ? menuAnchor.clientWidth : undefined, // Match the width of the anchor element (Select)
              // maxHeight: 400,
            },
          }
        }}
      >
        <Box sx={{ padding: "8px 16px" }}>
          <TextField
            fullWidth
            variant="outlined"
            size="small"
            placeholder="Søk..."
            value={searchTerm}
            onChange={handleSearchChange}
            inputRef={searchFieldRef}
            onClick={(e) => e.stopPropagation()} // Prevent menu from closing when clicking inside TextField
          />
        </Box>
        {filteredInputs.length > 0 ? (
          <FixedSizeList
            height={300}
            width="100%"
            itemSize={36}
            itemCount={filteredInputs.length}
            overscanCount={5}
          >
            {renderRow}
          </FixedSizeList>
        ) : (
          <Box sx={{ padding: 2, textAlign: "center" }}>
            <Typography variant="body2" color="textSecondary">
              Ingen resultater
            </Typography>
          </Box>
        )}
      </Menu>
    </FormControl>
  );
}

function arraysContainsSame(a: number[], b: number[]): boolean {
  const setA = new Set(a);
  const setB = new Set(b);

  if (setA.size !== setB.size) return true;

  for (const val of setA) {
    if (!setB.has(val)) return true;
  }

  return false;
}

interface ServerSearchItem {
  id: number;
  name: string;
}

interface serverSearchProps {
  label: string;
  tableName: string;
  selected: number[];
  singleSelect?: boolean;
  required?: boolean;
  error?: boolean;
  onChange: (ids: number[]) => void;
  onBlur?: React.FocusEventHandler<HTMLInputElement | HTMLTextAreaElement>;
}

export const SelectServerSearch: React.FC<serverSearchProps> = (props: serverSearchProps) => {
  const theme = useTheme();
  const {
    label, selected, tableName,
    singleSelect = false,
    required = false,
    error = false,
    onChange,
    onBlur = undefined
  } = props;

  const limit = 50;
  const noneOption = { id: 0, name: singleSelect ? 'Ingen' : 'Fjern alle' };

  const { api, error: getError } = useApi();
  const [options, setOptions] = useState<ServerSearchItem[]>([noneOption]);
  const [selectedItems, setSelectedItems] = useState<ServerSearchItem[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [pageIndex, setPageIndex] = useState(0);
  const [canLoadMore, setCanLoadMore] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [open, setOpen] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null); // For controlling menu open/close
  const searchFieldRef = React.useRef<HTMLInputElement>(null);

  React.useEffect(() => {
    // setPageIndex(0);
    // setSearchTerm("");

    // checks if selectedItems.id are different than selected, don't query if equal
    const selectedIds = selectedItems.map(item => item.id)
    if (!arraysContainsSame(selected, selectedIds)) {
      return;
    }

    if (selected && selected.length > 0) {
      const req = {
        table_name: tableName,
        selected_ids: selected,
      };

      api("/tables/search", "POST", req).then(res => {
        if (res.statusCode === 200) {
          const results = res.results ? res.results : [];
          console.log("setting selected:", results);
          setSelectedItems(results);
        }
      });
    }
  }, [selected]);

  // React.useEffect(() => {
  //   if (selected && selected.length > 0) {
  //     const req = {
  //       table_name: tableName,
  //       selected_ids: selected,
  //     };

  //     api("/tables/search", "POST", req).then(res => {
  //       if (res.statusCode === 200) {
  //         const results = res.results ? res.results : [];
  //         console.log("setting selected:", results);
  //         setSelectedItems(results);
  //       }
  //     });
  //   } else {
  //     setSelectedItems([]);
  //   }
  // }, [selected]);

  React.useEffect(() => {
    setPageIndex(0);
    setCanLoadMore(true);
    getOptions(0);
  }, [searchTerm]);

  const getOptions = (page: number) => {
    page === 0 ? setIsLoading(true) : setIsLoadingMore(true);

    const req = {
      table_name: tableName,
      search: searchTerm,
      limit: limit,
      page_index: page,
    };

    api("/tables/search", "POST", req).then(res => {
      if (res.statusCode === 200) {
        const results = res.results ? res.results : [];

        if (page === 0) {
          setOptions([noneOption, ...results]);
        } else {
          if (results.length > 0) {
            setOptions((prev) => [...prev, ...results]);
          }
        }

        if (results.length < limit) {
          setCanLoadMore(false);
        }
      }

      page === 0 ? setIsLoading(false) : setIsLoadingMore(false);
    });
  }

  const hendleLoadNextPage = () => {
    const nextPage = pageIndex + 1;
    setPageIndex(nextPage);
    getOptions(nextPage);
  }

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };

  const displaySelected = (selectedIds: number[]) => {
    const content = selectedIds.map((id) => {
      const selectedItem = selectedItems.find((input) => input.id === id);
      console.log("selectedId:", id, "items:", selectedItems, "displaying item:", selectedItem)
      if (selectedItem) {
        return singleSelect ?
          selectedItem.name :
          <Chip key={id} label={selectedItem.name} />;
      }
    })

    return <>{content}</>;
  }

  const handleChange = (value: number) => {
    // if value is in selected, remove it, else add it
    // if singleSelect, close the menu

    if (value === 0) {
      // clear click
      onChange([]);
      setSelectedItems([]);
      handleClose();
      return;
    }

    const itemClick = options.find((item) => item.id === value);
    let newSelected: number[];

    if (singleSelect) {
      newSelected = [value];
      setSelectedItems([itemClick]);
    } else {
      const selectedClick = selected.find((id) => id === value);
      if (selectedClick) {
        newSelected = selected.filter((id) => id !== value);
        setSelectedItems((prev) => prev.filter((item) => item.id !== value));
      } else {
        newSelected = [...selected, value];
        setSelectedItems((prev) => [...prev, itemClick]);
      }
    }

    onChange(newSelected);

    if (singleSelect) {
      handleClose();
    }
  };

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    setMenuAnchor(event.currentTarget);
    setTimeout(() => {
      if (searchFieldRef.current) {
        searchFieldRef.current.focus();
        searchFieldRef.current.select();
      }
    }, 0);
  };

  const handleClose = () => {
    setMenuAnchor(null);
  };

  const renderRow = ({ index, style }: { index: number; style: React.CSSProperties }) => {
    if (index > options.length - 2) {
      console.log(options.length, index);
    }

    if (index < options.length) {
      const item = options[index];
      const isSelected = selected.some((id) => id === item.id);

      return (
        <MenuItem
          key={item.id}
          value={item.id}
          sx={{
            fontWeight: theme.typography.fontWeightMedium,
            backgroundColor: isSelected ? alpha(theme.palette.primary.main, 0.1) : 'inherit',
            "&:hover": { backgroundColor: isSelected ? alpha(theme.palette.primary.main, 0.15) : '' }
          }}
          style={style}
          onClick={() => handleChange(item.id)}
        >
          {highlightText(item.name, searchTerm, theme.palette.mode)}
        </MenuItem>
      );
    } else if (index === options.length && canLoadMore) {
      return (
        <Button
          key={"load-more"}
          style={style}
          sx={{ textTransform: 'none' }}
          variant='contained'
          color='info'
          onClick={(e) => { e.stopPropagation(); hendleLoadNextPage() }}
          endIcon={isLoadingMore && <CircularProgress size={22} />}
        >
          Last mer...
        </Button>
      );
    } else {
      return (
        <Box sx={{ padding: 1, textAlign: "center" }} style={style}>
          <Typography variant="body2" color="textSecondary">
            Ingen flere resultater
          </Typography>
        </Box>
      );
    }
  };

  return (
    <FormControl variant='outlined' sx={{ width: '100%' }}>
      <InputLabel id="single-chip-label" error={error}>{label}{required ? " *" : ""}</InputLabel>
      <Select
        multiple
        labelId="single-chip-label"
        id="single-chip"
        value={selected}
        open={Boolean(menuAnchor)}
        onChange={(event) => event.preventDefault()}
        onOpen={handleOpen}
        onClose={handleClose}
        onBlur={onBlur}
        error={error}
        sx={{ "& .MuiSelect-icon": { color: theme.palette.mode === "dark" ? "white" : "black" } }}
        input={<OutlinedInput id="single-select-chip" label={`${label}${required ? " *" : ""}`} />}
        renderValue={(selectedIds) => (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {displaySelected(selectedIds)}
          </Box>
        )}
      >
        <Menu
          anchorEl={menuAnchor}
          open={Boolean(menuAnchor)}
          onClose={handleClose}
          slotProps={{
            paper: {
              style: {
                width: menuAnchor ? menuAnchor.clientWidth : undefined, // Match the width of the anchor element (Select)
                // maxHeight: 400,
              },
            }
          }}
        >
          <Box sx={{ padding: "8px 16px" }}>
            <TextField
              fullWidth
              variant="outlined"
              size="small"
              placeholder="Søk..."
              value={searchTerm}
              onChange={handleSearchChange}
              inputRef={searchFieldRef}
              onClick={(e) => e.stopPropagation()} // Prevent menu from closing when clicking inside TextField
            />
          </Box>
          <Box sx={{ height: 300 }}>
            <CenterSpinner loading={isLoading} error={getError}>
              {options.length > 1 ? (
                <FixedSizeList
                  height={300}
                  width="100%"
                  itemSize={36}
                  itemCount={options.length > limit ? options.length + 1 : options.length}
                  overscanCount={5}
                >
                  {renderRow}
                </FixedSizeList>
              ) : (
                <>
                  {options.length > 0 && (
                    <MenuItem
                      key={options[0].id}
                      value={options[0].id}
                      sx={{
                        fontWeight: theme.typography.fontWeightMedium,
                      }}
                      onClick={() => handleChange(options[0].id)}
                    >
                      {options[0].name}
                    </MenuItem>
                  )}
                  <Box sx={{ padding: 2, textAlign: "center" }}>
                    <Typography variant="body2" color="textSecondary">
                      Ingen resultater
                    </Typography>
                  </Box>
                </>
              )}
            </CenterSpinner>
          </Box>
        </Menu>
      </Select>
    </FormControl>
  );
}

interface VirtualizedListProps extends React.HTMLAttributes<HTMLElement> {
  options: any[];
  getOptionLabel: (option: any) => string;
  onSelect: (option: any) => void;
}

const VirtualizedList = forwardRef<HTMLDivElement, VirtualizedListProps>(
  ({ options, getOptionLabel, onSelect, className, role, id, "aria-labelledby": ariaLabelledby, ...otherProps }, ref) => {
    return (
      <div
        ref={ref}
        className={className}
        role={role}
        id={id}
        aria-labelledby={ariaLabelledby}
        {...otherProps} // Pass additional attributes required by Material-UI
      >
        <FixedSizeList
          height={300}
          width="100%"
          itemSize={36}
          itemCount={options.length}
          overscanCount={5}
        >
          {({ index, style }) => (
            <MenuItem
              style={style}
              onClick={() => onSelect(options[index])}
              role="option"
              aria-selected="false"
            >
              {getOptionLabel(options[index])}
            </MenuItem>
          )}
        </FixedSizeList>
      </div>
    );
  }
);

interface virtualizedAutocompleteProps {
  label: string;
  options: any[];
  selectField: string;
  selected: number;
  required?: boolean;
  error?: boolean;
  onSelect: (id: number) => void;
  onBlur?: React.FocusEventHandler<HTMLInputElement | HTMLTextAreaElement>;
}

export const VirtualizedAutocomplete: React.FC<virtualizedAutocompleteProps> = (props: virtualizedAutocompleteProps) => {
  const [inputValue, setInputValue] = React.useState("");

  const {
    label,
    options,
    selectField,
    selected,
    required = false,
    error = false,
    onSelect,
    onBlur = undefined,
  } = props;

  const filteredOptions = useMemo(
    () =>
      options.filter((option) =>
        option[selectField].toLowerCase().includes(inputValue.toLowerCase())
      ),
    [inputValue, options, selectField]
  );

  return (
    <Autocomplete
      options={filteredOptions}
      getOptionLabel={(option) => option[selectField]}
      value={selected > 0 ? options.find(item => item.id === selected) : null}
      onChange={(_, value) => {
        if (value) onSelect(value.id);
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          onChange={(e) => setInputValue(e.target.value)}
        />
      )}
      renderOption={() => null} // Disable default rendering
      ListboxComponent={(props) => ( // missing forwardRef! but cannot get it to work
        <VirtualizedList
          {...props}
          options={filteredOptions}
          getOptionLabel={(option) => option[selectField]}
          onSelect={(option) => onSelect(option.id)}
        />
      )}
    />
  );
}